import React, { useState, useCallback, useEffect } from 'react';
import {
  Button,
  Card,
  Checkbox,
  DatePicker,
  Form,
  Input,
  ModalProps,
  Radio,
  Row,
  Select,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import moment from 'moment';
import { LoadingOutlined } from '@ant-design/icons';
import { FormLabel, Loading, ScreenMask } from 'components/common';
import ModalDialog from 'components/common/ModalDialog';
import {
  COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
  COMMON_MSG_DEFAULT_DISPLAY_DURATION,
  HTTP_STATUS_OK,
  LOADING_ICON_SIZE1,
} from 'constants/config';
import { createLotNumber, editProductLot, fetchProductLot } from 'services/lot';
import { fetchLOTSettings } from 'services/lotSetting';

interface CreateProps extends ModalProps {
  generate: boolean;
  lotNumberGenerationType?: number;
  mode?: number; // bit 0: generate, bit 1: view, bit 2: edit, for example: 6 is view, edit but no generate
  noUniqueControl?: boolean;
  onClose: Function;
  onSave: Function;
  rows: any[];
  timeZone: string;
}

export default function Dialog(props: CreateProps) {
  const { rows, timeZone, onSave } = props;
  const [currentLot, setCurrentLot] = useState<StringKAnyVPair>({});
  const [generate, setGenerate] = useState(false);
  const [inited, setInited] = useState(false);
  const [isEditable, setIsEditable] = useState(false);
  const [loading, setLoading] = useState(false);
  const [noUniqueControl, setNoUniqueControl] = useState(false);
  const [lockScreen, setLockScreen] = useState(false);
  const [lotNumberGenerationType, setLotNumberGenerationType] = useState(0);
  const [detailDialogIsFullscreen, setDetailDialogIsFullscreen] =
    useState(false);
  const refreshBtnRef = React.useRef<any>(null);
  const [unique, setUnique] = useState<0 | 1>(0);
  const [form] = Form.useForm();

  // eslint-disable-next-line
  const boot = async () => {
    await getLOTSettings();

    if (props.mode && (props.mode & 2) === 2) {
      if (props.rows.length === 1) {
        const { lotNumber, productId } = props.rows[0];

        console.log('g', lotNumber, productId, props.rows[0]);
        (lotNumber && productId) && setTimeout(() => getLotInfo(productId, lotNumber), 0);
      }
    }
  };

  const dialogTitle = () => {
    let ret = generate ? 'Generate LOT#' : 'Manually Create New LOT#';

    (props.mode && ((props.mode & 2) === 2 || (props.mode & 4) === 4)) && (
      ret = 'LOT Information'
    );

    return ret;
  };

  const getLotInfo = async (productId: string, lotNumber: string) => {
    setLockScreen(true);
    try {
      const { code, data } = await fetchProductLot(productId, {lot: lotNumber}) as any;

      if (code === HTTP_STATUS_OK && Array.isArray(data)) {
        if (data.length === 1) {
          const ld = data[0];
          const fv = form.getFieldsValue();

          ld.expirationDate && (fv.ExpirationDate = moment(ld.expirationDate));
          fv.FirstInDate = moment(ld.firstInDate);
          fv.Status = ld.status;
          ld.manufactionDate && (fv.ManufactionDate = moment(ld.manufactionDate));
          ld.note && (fv.Note = ld.note);
          ld.supplierLot && (fv.SupplierLot = ld.supplierLot);
          //console.log('->', ld, fv);
          form.setFieldsValue(fv);
          setCurrentLot(ld);
        } else {
          message.info({
            content: 'No valid LOT data found',
            default: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
          });
        }
      } else {
        message.error({
          content: 'The returned LOT data format is incorrect',
          duration: COMMON_ERR_MSG_DEFAULT_DISPLAY_DURATION,
        });
      }
    } catch(e) {
    } finally {
      setLockScreen(false);
    }
  };

  const getLOTSettings = useCallback(async () => {
    setLockScreen(true);
    try {
      const res = await fetchLOTSettings();
      if (res.isSuccess) {
        setLotNumberGenerationType(res.data.generation);
        setNoUniqueControl(!!res.data.unique);
        if(res.data.generation === 1){
          setGenerate(true)
        }
      }
    } catch (error) {
    } finally {
      setLockScreen(false);
    }
  }, []);

  useEffect(()=>{
    if (!inited) {
      boot();
      setInited(true);
    }
  }, [boot, inited])

  const closeDetailDialog = () => {
    props.onClose();
  };

  const detailDialogWidth = useCallback(
    (isFullscreen = detailDialogIsFullscreen) => {
      return isFullscreen
        ? window.innerWidth - 4
        : window.innerWidth > 1280
        ? window.innerWidth * 0.8
        : 1200;
    },
    [detailDialogIsFullscreen]
  );

  const getDialogDom = () => {
    const refreshBtn = refreshBtnRef?.current;

    if (refreshBtn) {
      return refreshBtn.parentNode.parentNode.parentNode.parentNode.parentNode
        .parentNode.parentNode;
    }

    return null;
  };

  const onFullscreen = (isFullscreen: boolean) => {
    const dialog = getDialogDom();

    setDetailDialogIsFullscreen(isFullscreen);

    if (dialog) {
      const width = detailDialogWidth(isFullscreen);

      dialog.style.width = `${width}px`;
    }
  };

  const createLot = async () => {
    const params = await form.validateFields();
    if (params.errorFields) return;
    console.log(params);
    try {
      setLoading(true);
      //todo generate request
      const res = await createLotNumber({
        TimeZone: timeZone,
        Generate: generate,
        Unique: !!unique,
        Items: rows.map((i) => {
          return {
            SKU: i.sku,
            PolineNum: i.poLineNum,
            LotNumber: params.LotNumber,
            FirstInDate: params.FirstInDate,
            ExpirationDate: params.ExpirationDate,
            ManufactionDate: params.ManufactionDate,
            SupplierLot: params.SupplierLot,
            Note: params.Note,
          };
        }),
      });
      setLoading(false);
      if (res.isSuccess) {
        message.success({ content: 'Save successfully' });
        const t: { [key: string]: any } = {};
        res.data.map((i: any, index: number) => {
          t[i.poLineNum || rows[index].poLineNum || rows[index].sku] = i.lotNumber;
          return true;
        });
        onSave(t);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  const editLot = async () => {
    const params = await form.validateFields();

    //console.log('e', currentLot);
    if (params.errorFields) return;
    if (!(currentLot.productLotNum > 0)) return;

    //console.log('p', params, currentLot);
    params.FulfillmentProductNum = currentLot.fulfillmentProductNum;
    params.ProductLotNum = currentLot.productLotNum;
    setLockScreen(true);
    try {
      const { code, isSuccess, data } = await editProductLot(params);

      if (code === HTTP_STATUS_OK && isSuccess) {
        message.success({
          content: 'Edited lot successfully',
          duration: COMMON_MSG_DEFAULT_DISPLAY_DURATION,
        });
        setTimeout(() => {
          onSave(data);
        }, 0);
      }
    } catch(e) {
      console.log('Editing failed');
    } finally {
      setLockScreen(false);
    }
  };

  const handleSave = () => {
    const isEdit = (props.mode && ((props.mode & 4) === 4)) ? true : false;

    if (isEdit) {
      editLot();
    } else {
      createLot();
    }
  };

  const isEditMode = (checkEdit = false) => {
    const ms = props.mode && ((props.mode & 4) === 4);

    return checkEdit ? (ms && isEditable) : ms;
  };

  const isViewMode = (checkEdit = false) => {
    const ms = !!(props.mode && ((props.mode & 2) === 2));

    return checkEdit ? (ms && !isEditable) : ms;
  };

  const toggleEditable = () => {
    if (isEditable) {
      isViewMode() && setIsEditable(false);
    } else {
      isEditMode() && setIsEditable(true);
    }
  };

  const layout = {
    labelCol: { span: 8 },
    wrapperCol: { span: 12 },
  };
  return (
    <>
      <ModalDialog
        centered
        className="fullscreen-modal"
        closable={false}
        closeButton={true}
        footer={
          <Space>
            <Button onClick={() => closeDetailDialog()}>Cancel</Button>
            {(props.mode ? (props.mode === 1 || isEditMode(true)) : true) && (
              <Button type="primary" onClick={() => handleSave()}>
                {loading ? 'Saving' : 'Save'}
              </Button>
            )}
            {isViewMode(true) && (
              <Button
                onClick={toggleEditable}
                type="primary"
              >
                Edit
              </Button>
            )}
          </Space>
        }
        fullscreen={true}
        maskClosable={false}
        onClose={closeDetailDialog}
        onFullscreen={onFullscreen}
        //title={generate ? 'Generate LOT#' : 'Manually Create New LOT#'}
        title={dialogTitle()}
        visible={props.visible}
        width={detailDialogWidth()}
      >
        <Card style={{ height: '100%' }}>
          <Spin
            spinning={loading}
            indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}
          >
            <Form
              {...layout}
              form={form}
              style={{ marginTop: 20 }}
              onFinish={handleSave}
            >
              <Row justify="center" style={{ marginBottom: 10 }}>
                {rows.length > 1 && !noUniqueControl && (
                  <Typography.Paragraph>
                    All lines will have the same LOT#.
                  </Typography.Paragraph>
                )}
                {rows.length > 1 && noUniqueControl && (
                  <Radio.Group
                    value={unique}
                    onChange={(e) => setUnique(e.target.value)}
                  >
                    <Radio value={1}>Each line has a unique LOT#</Radio>
                    <Radio value={0}>All Line have the same LOT#</Radio>
                  </Radio.Group>
                )}
              </Row>

              <Form.Item label={<FormLabel>LOT#</FormLabel>}>
                <Space>
                  {!generate && (
                    <Form.Item
                      noStyle
                      name="LotNumber"
                      initialValue={rows.length === 1 ? rows[0].lotNumber : undefined}
                      rules={[
                        { required: true, message: 'Please input LOT#!' },
                      ]}
                    >
                      <Input disabled={props.mode ? ((props.mode & 1) !== 1) : false} />
                    </Form.Item>
                  )}
                  {(lotNumberGenerationType !== 2 && (props.mode ? ((props.mode & 1) === 1) : true)) && (
                    <Checkbox
                      checked={generate}
                      onChange={(e) => {
                        setGenerate(e.target.checked);
                        form.setFieldsValue({ LotNumber: undefined });
                      }}
                    >
                      Automatically generate
                    </Checkbox>
                  )}
                </Space>
              </Form.Item>

              {props.mode && ((props.mode & 2) === 2 || (props.mode & 4) === 4) && currentLot.productLotNum > 0 && (<>
                <Form.Item
                  name="Status"
                  label={<FormLabel>Status</FormLabel>}
                  rules={[
                  ]}
                >
                  <Select disabled={isViewMode(true)}>
                    <Select.Option value={1}>Active</Select.Option>
                    <Select.Option value={0}>InActive</Select.Option>
                  </Select>
                </Form.Item>
              </>)}

              <Form.Item
                name="FirstInDate"
                label={<FormLabel>First In Date</FormLabel>}
                initialValue={moment()}
                rules={[
                  { required: true, message: 'Please input First In Date!' },
                ]}
              >
                <DatePicker disabled={isViewMode(true)} />
              </Form.Item>

              <Form.Item
                name="ExpirationDate"
                label={<FormLabel>Expiration Date</FormLabel>}
              >
                <DatePicker disabled={isViewMode(true)} />
              </Form.Item>

              <Form.Item
                name="ManufactionDate"
                label={<FormLabel>Manufacuring Date</FormLabel>}
                extra="You can set the default manufacturing date to the current date upon receipt and override it to the date of manufacture at the supplier, if applicable."
              >
                <DatePicker disabled={isViewMode(true)} />
              </Form.Item>

              <Form.Item
                name="SupplierLot"
                label={<FormLabel>Supplier LOT#</FormLabel>}
              >
                <Input disabled={isViewMode(true)} />
              </Form.Item>

              <Form.Item name="Note" label={<FormLabel>Note</FormLabel>}>
                <Input.TextArea rows={2} disabled={isViewMode(true)} />
              </Form.Item>
            </Form>
          </Spin>
        </Card>
      </ModalDialog>
      {lockScreen && (
        <ScreenMask>
          <Loading size={LOADING_ICON_SIZE1} />
        </ScreenMask>
      )}
    </>
  );
}
